import React, { Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";
import { motion } from "framer-motion";
import { 
  Shield, 
  Award, 
  Users, 
  ExternalLink, 
  Handshake,
  Building,
  GraduationCap,
  Car,
  ShoppingBag
} from 'lucide-react';

// Préchargement du modèle
useGLTF.preload("/models/2020__mclaren_sabre.glb");

function Model() {
  const { scene } = useGLTF("/models/2020__mclaren_sabre.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className="fixed inset-0">
      <color attach="background" args={["#121F45"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

const Partenaires = () => {
  const poleAuto = {
    title: "Pôle Automobile",
    icon: <Car className="w-8 h-8" />,
    features: [
      {
        icon: <Building className="w-6 h-6" />,
        title: "Partenaires Techniques",
        items: [
          "Équipementiers automobiles",
          "Fournisseurs de pièces détachées",
        ]
      },
      {
        icon: <GraduationCap className="w-6 h-6" />,
        title: "Partenaires Formation",
        items: [
          "Opco Mobilité",
          "ANFA Association Nationale pour la Formation Automobile",
          "GNFA Groupement National pour la Formation Automobile",
        ]
      },
      {
        icon: <Handshake className="w-6 h-6" />,
        title: "Partenaires Commerciaux",
        items: [
          "Concessionnaires automobiles",
          "Réseaux de garages",
        ]
      }
    ],
    partners: [
      {
        name: 'AutoTech Solutions',
        logo: <Shield className="w-12 h-12" />,
        description: 'Leader dans les solutions automobiles innovantes',
        speciality: 'Diagnostic automobile',
        website: 'https://autotech.example.com'
      },
      {
        name: 'MécaPro France',
        logo: <Award className="w-12 h-12" />,
        description: 'Expert en formation mécanique professionnelle',
        speciality: 'Formation technique',
        website: 'https://mecapro.example.com'
      },
    ]
  };

  const poleVente = {
    title: "Pôle Supérieur Vente",
    icon: <ShoppingBag className="w-8 h-8" />,
    features: [
      {
        icon: <Building className="w-6 h-6" />,
        title: "Partenaires Commerciaux",
        items: [
          "Grandes enseignes",
          "Commerces de proximité",
        ]
      },
      {
        icon: <GraduationCap className="w-6 h-6" />,
        title: "Partenaires Formation",
        items: [
          "CCI",
          "Organisations professionnelles",
          "Écoles de commerce",
        ]
      },
      {
        icon: <Handshake className="w-6 h-6" />,
        title: "Partenaires Institutionnels",
        items: [
          "Chambres de commerce",
          "Associations professionnelles",
        ]
      }
    ],
    partners: [
      {
        name: 'Digital Retail',
        logo: <Users className="w-12 h-12" />,
        description: 'Solutions numériques pour le commerce',
        speciality: 'Innovation commerciale',
        website: 'https://digitalretail.example.com'
      },
      {
        name: 'Sales Academy',
        logo: <Award className="w-12 h-12" />,
        description: 'Formation aux techniques de vente modernes',
        speciality: 'Formation commerciale',
        website: 'https://salesacademy.example.com'
      },
    ]
  };

  const renderPoleSection = (pole, isGreen = false) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-20"
    >
      <motion.div className="flex items-center justify-center space-x-4 mb-12">
        <div className={isGreen ? "text-green-400" : "text-blue-400"}>
          {pole.icon}
        </div>
        <h2 className="text-3xl font-bold text-white">{pole.title}</h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto"
      >
        {pole.features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className={`bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 hover:bg-slate-700/60 transition-colors cursor-pointer border-l-4 ${
              isGreen ? 'border-green-500 hover:border-emerald-500' : 'border-blue-500 hover:border-pink-500'
            }`}
          >
            <div className="flex items-start space-x-4">
              <motion.div 
                className="p-3 bg-slate-700/50 rounded-lg"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <div className={isGreen ? "text-green-400" : "text-blue-400"}>
                  {feature.icon}
                </div>
              </motion.div>
              <div className="flex-grow">
                <h3 className="text-xl font-semibold text-white mb-4">
                  {feature.title}
                </h3>
                <ul className="space-y-2">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center space-x-2 text-gray-300">
                      <motion.div 
                        className={`h-1.5 w-1.5 ${
                          isGreen ? 'bg-green-400' : 'bg-blue-400'
                        } rounded-full`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          duration: 0.5,
                          delay: index * 0.1 + itemIndex * 0.1
                        }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2 mt-4">
                  <motion.div 
                    className={`h-1.5 ${
                      isGreen ? 'bg-green-600/50' : 'bg-blue-600/50'
                    } rounded-full`}
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: index * 0.2
                    }}
                  />
                  <motion.div 
                    className={`h-1.5 ${
                      isGreen ? 'bg-green-400/50' : 'bg-blue-400/50'
                    } rounded-full`}
                    initial={{ width: "0%" }}
                    animate={{ width: "75%" }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: index * 0.3
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto"
      >
        {pole.partners.map((partner, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 + 0.3 }}
            whileHover={{ scale: 1.02 }}
            className={`bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 hover:bg-slate-700/60 transition-colors border-l-4 ${
              isGreen ? 'border-green-500 hover:border-emerald-500' : 'border-blue-500 hover:border-pink-500'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <motion.div 
                className="p-3 bg-slate-700/50 rounded-lg"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <div className={isGreen ? "text-green-400" : "text-blue-400"}>
                  {partner.logo}
                </div>
              </motion.div>
              <motion.a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={isGreen ? "text-green-400 hover:text-green-300" : "text-blue-400 hover:text-blue-300"}
              >
                <ExternalLink className="w-5 h-5" />
              </motion.a>
            </div>
            
            <h3 className="text-xl font-bold text-white mb-2">
              {partner.name}
            </h3>
            
            <p className="text-gray-300 mb-4">
              {partner.description}
            </p>
            
            <div className={`${
              isGreen ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
            } px-3 py-1 rounded-full text-sm inline-block`}>
              {partner.speciality}
            </div>

            <div className="space-y-2 mt-4">
              <motion.div 
                className={`h-1.5 ${
                  isGreen ? 'bg-green-600/50' : 'bg-blue-600/50'
                } rounded-full`}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: index * 0.2
                }}
              />
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen py-40"
    >
      <div className="fixed inset-0 z-0">
        <CarScene />
      </div>

      <Loader />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.h1
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-6xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500"
          >
            Nos Partenaires
          </motion.h1>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-xl text-center text-white font-medium max-w-2xl mx-auto mb-12 bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm"
          >
            Découvrez notre réseau de partenaires qui nous accompagnent dans notre mission
          </motion.p>
        </motion.div>

        {renderPoleSection(poleAuto, false)}
        {renderPoleSection(poleVente, true)}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-20"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Handshake className="w-4 h-4" />
            <span>Devenir partenaire</span>
          </motion.button>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Users className="w-4 h-4" />
            <span>Nous contacter</span>
          </motion.button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Partenaires;